import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  AdminGuidesResponse,
  GuideEditorResponse,
  GuideReadResponse,
  GuideSearchResponse,
  GuidesIndexResponse,
} from '@lp/shared';
import { api } from './api.js';

/**
 * Every guides read, in one place, so a write can invalidate exactly what it
 * changed. Keys follow the repo convention: the resource first.
 */
export const guideKeys = {
  index: () => ['guides'] as const,
  guide: (sectionSlug: string, guideSlug: string) => ['guide', sectionSlug, guideSlug] as const,
  search: (q: string) => ['guides', 'search', q] as const,
  admin: () => ['admin', 'guides'] as const,
  editor: (guideId: string) => ['admin', 'guide', guideId] as const,
};

export function useGuidesIndex(): UseQueryResult<GuidesIndexResponse> {
  return useQuery({
    queryKey: guideKeys.index(),
    queryFn: ({ signal }) => api.get<GuidesIndexResponse>('/guides', signal),
  });
}

export function useGuide(sectionSlug: string, guideSlug: string): UseQueryResult<GuideReadResponse> {
  return useQuery({
    queryKey: guideKeys.guide(sectionSlug, guideSlug),
    queryFn: ({ signal }) =>
      api.get<GuideReadResponse>(
        `/guides/${encodeURIComponent(sectionSlug)}/${encodeURIComponent(guideSlug)}`,
        signal,
      ),
    enabled: sectionSlug.length > 0 && guideSlug.length > 0,
  });
}

/** Search as you type. The previous results stay on screen while the next load. */
export function useGuideSearch(q: string): UseQueryResult<GuideSearchResponse> {
  const query = q.trim();
  return useQuery({
    queryKey: guideKeys.search(query),
    queryFn: ({ signal }) =>
      api.get<GuideSearchResponse>(`/guides/search?q=${encodeURIComponent(query)}`, signal),
    enabled: query.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useAdminGuides(): UseQueryResult<AdminGuidesResponse> {
  return useQuery({
    queryKey: guideKeys.admin(),
    queryFn: ({ signal }) => api.get<AdminGuidesResponse>('/admin/guides', signal),
  });
}

export function useGuideEditor(guideId: string): UseQueryResult<GuideEditorResponse> {
  return useQuery({
    queryKey: guideKeys.editor(guideId),
    queryFn: ({ signal }) =>
      api.get<GuideEditorResponse>(`/admin/guides/${encodeURIComponent(guideId)}`, signal),
    enabled: guideId.length > 0,
  });
}
